import type { Market } from "../../domain/location/types";
import { calculateRunningCost } from "../../domain/runningCost/calculateRunningCost";
import { formatAreaM2 } from "../../domain/units/formatUnits";
import type {
	CalculationResult,
	HeaterType,
	ThermalInputs,
} from "../../engines/types";
import { type Dictionary, formatPrice } from "../../i18n";

/**
 * Pure model mapper for the live running-cost readout in the builder
 * (heat-up time + cost per session). React-free and side-effect-free: it
 * consumes values the builder has already derived — the split-aware sauna
 * volume and the heater requirement — and returns fully-formed display
 * strings.
 *
 * Nothing here recomputes materials or touches the Zustand store; the summary
 * is a presentation layer over data `useBuilderDerivedData` has already
 * memoized. See docs/notes/2026-06-24-cross-cutting-insight-views-need-a-pure-
 * model-mapper.md.
 *
 * Currency contract: the domain works in SEK and `formatPrice` converts at the
 * market boundary — the same pattern as SharePage / buildSharedDesignSummary.
 * No currency symbol or SEK assumption may leak into a component.
 *
 * Every label here is built from the domain's numeric fields plus dictionary
 * keys; the domain returns no display strings of its own.
 */

export interface BuilderRunningCostSummaryInput {
	/**
	 * The engine result. Only used as a fallback source for volume/heater —
	 * every slice is read defensively because a degraded or mocked calcData
	 * carries no enrichment slices at all. See
	 * docs/notes/2026-07-10-workspace-badge-mapper-must-tolerate-partial-calcdata.md
	 */
	calcData?: Partial<
		Pick<CalculationResult, "volume" | "heaterRequirements" | "glassPenalty">
	> | null;
	/** Split-aware sauna-room volume in m3 (preferred over `calcData.volume`). */
	volumeM3?: number;
	/** Recommended heater power in kW (preferred over the calcData slice). */
	heaterKw?: number;
	thermalInputs?: Partial<ThermalInputs> | null;
	market: Market;
	t: Dictionary;
}

export interface BuilderRunningCostSummary {
	heaterType: HeaterType;
	/**
	 * Recommended electric heater power, or `null` — `recommendedKw` sizes an
	 * ELECTRIC element and the wood model never reads it, so the wood branch
	 * has no power to report and consumers must omit the row rather than
	 * print "0 kW".
	 */
	heaterKw: number | null;
	heatUpMinutes: number;
	kwhPerSession: number;
	sessionCostSek: number;
	costPerMonthSek: number;
	/** "42 min" */
	heatUpLabel: string;
	/** "1,2 m²" — glass bounding the hot room, read off the built openings. */
	glassAreaLabel: string;
	/**
	 * Market-formatted, tilde-prefixed. Uses the small-amount formatter: a
	 * session costs a few kronor, which whole-unit rounding would show as 0
	 * on intl.
	 */
	sessionCostLabel: string;
	monthlyCostLabel: string;
	/** "8 kW", or `null` on the wood branch — see `heaterKw`. */
	heaterKwLabel: string | null;
	/** Localized caption telling electricity cost apart from firewood cost. */
	costNote: string;
	/** Spoken form for surfaces that fold the numbers into an aria-label. */
	ariaSummary: string;
}

/** First positive, finite candidate — 0 and NaN are "missing", not "free". */
function firstUsableNumber(
	...candidates: (number | undefined)[]
): number | null {
	for (const candidate of candidates) {
		if (
			typeof candidate === "number" &&
			Number.isFinite(candidate) &&
			candidate > 0
		) {
			return candidate;
		}
	}
	return null;
}

/**
 * Build the live running-cost readout, or `null` when the inputs cannot
 * support one (missing volume, missing heater power on an electric heater).
 * Callers render nothing on `null` rather than showing a placeholder number.
 */
export function buildRunningCostSummary({
	calcData,
	volumeM3,
	heaterKw,
	thermalInputs,
	market,
	t,
}: BuilderRunningCostSummaryInput): BuilderRunningCostSummary | null {
	const heaterType: HeaterType =
		thermalInputs?.heaterType === "electric" ? "electric" : "wood";

	const volume = firstUsableNumber(volumeM3, calcData?.volume);
	if (volume === null) return null;

	const power = firstUsableNumber(
		heaterKw,
		calcData?.heaterRequirements?.recommendedKw,
	);
	// The wood model is fuel-based and never divides by the heater power, so a
	// missing kW only blocks the electric branch (which would divide by zero).
	if (power === null && heaterType === "electric") return null;

	// The hot room's glass as the engine built it (issue 165) — the same figure
	// the heater was sized on, not the retired manual field.
	const glassAreaSqm = calcData?.glassPenalty?.totalGlassAreaSqm ?? 0;

	const result = calculateRunningCost({
		// Ignored by the wood branch, which is the only way `power` is null here.
		heaterKw: power ?? 0,
		volumeM3: volume,
		heaterType,
		glassAreaSqm,
		isOutdoorLogCabin: thermalInputs?.isOutdoorLogCabin ?? false,
		market,
	});

	if (!Number.isFinite(result.heatUpMinutes)) return null;

	const heatUpLabel = `${result.heatUpMinutes} ${t.runningCost_minutesShort}`;
	const sessionCostLabel = `~${formatPrice(result.sessionCostSek, market, { maxFractionDigits: 1 })}`;
	const monthlyCostLabel = `~${formatPrice(result.costPerMonthSek, market)}`;

	const electricPower = heaterType === "electric" ? power : null;

	return {
		heaterType,
		heaterKw: electricPower,
		heatUpMinutes: result.heatUpMinutes,
		kwhPerSession: result.kwhPerSession,
		sessionCostSek: result.sessionCostSek,
		costPerMonthSek: result.costPerMonthSek,
		heatUpLabel,
		glassAreaLabel: formatAreaM2(
			glassAreaSqm,
			"metric",
			market === "se" ? "sv-SE" : "en-US",
		),
		sessionCostLabel,
		monthlyCostLabel,
		heaterKwLabel:
			electricPower === null
				? null
				: `${electricPower} ${t.runningCost_kwShort}`,
		costNote:
			heaterType === "wood"
				? t.runningCost_woodNote
				: t.runningCost_electricNote,
		ariaSummary: t.runningCost_ariaSummary
			.replaceAll("{minutes}", String(result.heatUpMinutes))
			.replaceAll(
				"{cost}",
				formatPrice(result.sessionCostSek, market, { maxFractionDigits: 1 }),
			),
	};
}
